import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SignInBox from './SignInBox';
import SignUpBox from './SignUpBox';

const SignInRequiredModal = ({
  isOpen,
  onClose,
  isUserSignedIn,
  handleLogout,
  user,
  setIsUserSignedIn,
  setUser,
  setDoctors,
  setInsurances,
  setBodilyParts,
  setOriginalDoctors,
}) => {
  const navigate = useNavigate();
  const [activeMode, setActiveMode] = useState('signup');

  useEffect(() => {
    if (isOpen) {
      setActiveMode('signup');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isSignInMode = activeMode === 'signin';

  const handleSuccessfulSignUp = () => {
    navigate('/', { replace: true });
    onClose();
  };

  const handleSuccessfulSignIn = () => {
    navigate('/', { replace: true });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-950/85 px-4 py-5 backdrop-blur-md sm:px-6">
      <style>
        {`
          @keyframes subtitleTsunamiWaveDrift {
            0%, 100% {
              transform: translate3d(-10%, 0, 0);
            }

            25% {
              transform: translate3d(-5%, -1.5%, 0);
            }

            50% {
              transform: translate3d(0%, -2.5%, 0);
            }

            75% {
              transform: translate3d(-5%, -1.5%, 0);
            }
          }

          @keyframes subtitleTsunamiWaveRise {
            0%, 100% {
              transform: translate3d(0, 36%, 0);
              opacity: 0.58;
              filter: hue-rotate(0deg) saturate(1.12);
            }

            25% {
              transform: translate3d(0, 23%, 0);
              opacity: 0.74;
              filter: hue-rotate(42deg) saturate(1.24);
            }

            50% {
              transform: translate3d(0, 12%, 0);
              opacity: 0.9;
              filter: hue-rotate(96deg) saturate(1.42);
            }

            75% {
              transform: translate3d(0, 23%, 0);
              opacity: 0.74;
              filter: hue-rotate(42deg) saturate(1.24);
            }
          }

          @keyframes subtitleTsunamiFoamDrift {
            0%, 100% {
              transform: translate3d(-18%, 0, 0);
              opacity: 0.32;
            }

            25% {
              transform: translate3d(-8%, -3%, 0);
              opacity: 0.5;
            }

            50% {
              transform: translate3d(2%, -5%, 0);
              opacity: 0.72;
            }

            75% {
              transform: translate3d(-8%, -3%, 0);
              opacity: 0.5;
            }
          }

          @keyframes subtitleTsunamiCrashSweep {
            0%, 100% {
              transform: translate3d(-115%, 18%, 0) rotate(-18deg);
              opacity: 0;
            }

            35% {
              transform: translate3d(-60%, 4%, 0) rotate(-18deg);
              opacity: 0;
            }

            50% {
              transform: translate3d(4%, -18%, 0) rotate(-18deg);
              opacity: 0.42;
            }

            65% {
              transform: translate3d(92%, -32%, 0) rotate(-18deg);
              opacity: 0;
            }
          }

          @keyframes subtitleTsunamiGlowPulse {
            0%, 100% {
              opacity: 0.3;
              transform: scale(1);
            }

            50% {
              opacity: 0.58;
              transform: scale(1.08);
            }
          }

          @keyframes subtitleTsunamiSurfaceTilt {
            0%, 100% {
              transform: translate3d(0, 0, 0) rotate(-0.7deg);
            }

            25% {
              transform: translate3d(0.5%, -1.5%, 0) rotate(0.5deg);
            }

            50% {
              transform: translate3d(0, -2.5%, 0) rotate(0.9deg);
            }

            75% {
              transform: translate3d(0.5%, -1.5%, 0) rotate(0.5deg);
            }
          }
        `}
      </style>

      <div className="flex min-h-full items-center justify-center">
        <div className="relative w-full max-w-[1380px] overflow-visible rounded-[2rem] border border-cyan-300/20 bg-slate-950 shadow-2xl shadow-cyan-950/50">
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-cyan-300/15 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-28 -left-24 h-80 w-80 rounded-full bg-emerald-300/10 blur-3xl" />

          <button
            type="button"
            onClick={onClose}
            aria-label="Close sign in modal"
            className="absolute -right-4 -top-4 z-50 inline-flex h-12 w-12 items-center justify-center rounded-full border border-cyan-200/25 bg-slate-900/95 text-2xl font-black text-slate-100 shadow-2xl shadow-slate-950/60 backdrop-blur-md transition hover:-translate-y-0.5 hover:scale-105 hover:border-rose-300/50 hover:bg-rose-400/15 hover:text-rose-100"
          >
            ×
          </button>

          <div className="relative z-10 overflow-hidden rounded-[2rem]">
            <div className="grid lg:grid-cols-[340px_minmax(0,1fr)]">
              <aside className="relative hidden overflow-hidden border-r border-white/10 bg-gradient-to-br from-slate-950 via-slate-950 to-cyan-950/40 lg:block">
                <div className="pointer-events-none absolute inset-0">
                  <div
                    className="absolute bottom-[-8rem] left-[-8rem] h-96 w-96 rounded-full bg-cyan-300/25 blur-3xl"
                    style={{
                      animation:
                        'subtitleTsunamiGlowPulse 8s ease-in-out infinite',
                    }}
                  />

                  <div
                    className="absolute bottom-[6rem] right-[-9rem] h-80 w-80 rounded-full bg-emerald-300/20 blur-3xl"
                    style={{
                      animation:
                        'subtitleTsunamiGlowPulse 10s ease-in-out infinite reverse',
                    }}
                  />

                  <div
                    className="absolute inset-x-0 bottom-[-4rem] h-[34rem]"
                    style={{
                      animation:
                        'subtitleTsunamiWaveRise 12s ease-in-out infinite',
                    }}
                  >
                    <svg
                      className="absolute bottom-0 left-0 h-full w-[150%]"
                      viewBox="0 0 900 520"
                      preserveAspectRatio="none"
                      style={{
                        animation:
                          'subtitleTsunamiWaveDrift 12s ease-in-out infinite',
                      }}
                    >
                      <defs>
                        <linearGradient
                          id="subtitleWaveGradient"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="0%"
                        >
                          <stop
                            offset="0%"
                            stopColor="rgba(34, 211, 238, 0.22)"
                          />
                          <stop
                            offset="35%"
                            stopColor="rgba(16, 185, 129, 0.52)"
                          />
                          <stop
                            offset="68%"
                            stopColor="rgba(59, 130, 246, 0.46)"
                          />
                          <stop
                            offset="100%"
                            stopColor="rgba(103, 232, 249, 0.28)"
                          />
                        </linearGradient>

                        <linearGradient
                          id="subtitleFoamGradient"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="0%"
                        >
                          <stop
                            offset="0%"
                            stopColor="rgba(240, 253, 250, 0.18)"
                          />
                          <stop
                            offset="45%"
                            stopColor="rgba(165, 243, 252, 0.86)"
                          />
                          <stop
                            offset="100%"
                            stopColor="rgba(209, 250, 229, 0.22)"
                          />
                        </linearGradient>
                      </defs>

                      <g
                        style={{
                          animation:
                            'subtitleTsunamiSurfaceTilt 12s ease-in-out infinite',
                          transformOrigin: 'center bottom',
                        }}
                      >
                        <path
                          fill="url(#subtitleWaveGradient)"
                          d="M0 260 C90 188 170 204 250 250 C340 306 414 304 510 230 C620 146 724 172 900 250 L900 520 L0 520 Z"
                        >
                          <animate
                            attributeName="d"
                            dur="12s"
                            repeatCount="indefinite"
                            values="
                              M0 260 C90 188 170 204 250 250 C340 306 414 304 510 230 C620 146 724 172 900 250 L900 520 L0 520 Z;
                              M0 246 C118 226 212 230 300 244 C390 258 468 252 556 220 C666 180 770 198 900 236 L900 520 L0 520 Z;
                              M0 270 C92 196 176 202 250 238 C338 282 404 288 488 220 C594 134 710 124 900 252 L900 520 L0 520 Z;
                              M0 246 C118 226 212 230 300 244 C390 258 468 252 556 220 C666 180 770 198 900 236 L900 520 L0 520 Z;
                              M0 260 C90 188 170 204 250 250 C340 306 414 304 510 230 C620 146 724 172 900 250 L900 520 L0 520 Z
                            "
                            keyTimes="0;0.25;0.5;0.75;1"
                            calcMode="spline"
                            keySplines="
                              .45 0 .55 1;
                              .45 0 .55 1;
                              .45 0 .55 1;
                              .45 0 .55 1
                            "
                          />
                        </path>

                        <path
                          fill="none"
                          stroke="url(#subtitleFoamGradient)"
                          strokeWidth="10"
                          strokeLinecap="round"
                          opacity="0.94"
                          d="M0 262 C90 190 170 206 250 252 C340 308 414 306 510 232 C620 148 724 174 900 252"
                        >
                          <animate
                            attributeName="d"
                            dur="12s"
                            repeatCount="indefinite"
                            values="
                              M0 262 C90 190 170 206 250 252 C340 308 414 306 510 232 C620 148 724 174 900 252;
                              M0 248 C118 228 212 232 300 246 C390 260 468 254 556 222 C666 182 770 200 900 238;
                              M0 272 C92 198 176 204 250 240 C338 284 404 290 488 222 C594 136 710 126 900 254;
                              M0 248 C118 228 212 232 300 246 C390 260 468 254 556 222 C666 182 770 200 900 238;
                              M0 262 C90 190 170 206 250 252 C340 308 414 306 510 232 C620 148 724 174 900 252
                            "
                            keyTimes="0;0.25;0.5;0.75;1"
                            calcMode="spline"
                            keySplines="
                              .45 0 .55 1;
                              .45 0 .55 1;
                              .45 0 .55 1;
                              .45 0 .55 1
                            "
                          />
                        </path>

                        <path
                          fill="rgba(14, 165, 233, 0.2)"
                          d="M0 302 C110 244 190 264 276 314 C370 368 470 360 570 292 C684 214 772 236 900 300 L900 520 L0 520 Z"
                        >
                          <animate
                            attributeName="d"
                            dur="12s"
                            repeatCount="indefinite"
                            values="
                              M0 302 C110 244 190 264 276 314 C370 368 470 360 570 292 C684 214 772 236 900 300 L900 520 L0 520 Z;
                              M0 294 C120 280 222 282 324 292 C442 306 552 298 662 278 C748 264 826 274 900 292 L900 520 L0 520 Z;
                              M0 310 C106 258 198 270 284 304 C380 342 474 338 570 284 C676 224 776 230 900 306 L900 520 L0 520 Z;
                              M0 294 C120 280 222 282 324 292 C442 306 552 298 662 278 C748 264 826 274 900 292 L900 520 L0 520 Z;
                              M0 302 C110 244 190 264 276 314 C370 368 470 360 570 292 C684 214 772 236 900 300 L900 520 L0 520 Z
                            "
                            keyTimes="0;0.25;0.5;0.75;1"
                            calcMode="spline"
                            keySplines="
                              .45 0 .55 1;
                              .45 0 .55 1;
                              .45 0 .55 1;
                              .45 0 .55 1
                            "
                          />
                        </path>

                        <path
                          fill="rgba(6, 78, 59, 0.3)"
                          d="M0 332 C110 286 196 300 286 344 C390 394 492 392 600 330 C716 264 790 278 900 334 L900 520 L0 520 Z"
                        >
                          <animate
                            attributeName="d"
                            dur="12s"
                            repeatCount="indefinite"
                            values="
                              M0 332 C110 286 196 300 286 344 C390 394 492 392 600 330 C716 264 790 278 900 334 L900 520 L0 520 Z;
                              M0 326 C130 314 250 316 360 326 C480 336 602 330 722 316 C800 308 856 316 900 326 L900 520 L0 520 Z;
                              M0 340 C104 300 208 306 300 336 C410 372 510 372 608 320 C724 260 802 278 900 340 L900 520 L0 520 Z;
                              M0 326 C130 314 250 316 360 326 C480 336 602 330 722 316 C800 308 856 316 900 326 L900 520 L0 520 Z;
                              M0 332 C110 286 196 300 286 344 C390 394 492 392 600 330 C716 264 790 278 900 334 L900 520 L0 520 Z
                            "
                            keyTimes="0;0.25;0.5;0.75;1"
                            calcMode="spline"
                            keySplines="
                              .45 0 .55 1;
                              .45 0 .55 1;
                              .45 0 .55 1;
                              .45 0 .55 1
                            "
                          />
                        </path>
                      </g>
                    </svg>
                  </div>

                  <div
                    className="absolute bottom-[31%] left-[-4rem] h-16 w-[150%] rounded-full bg-[radial-gradient(circle_at_10%_50%,rgba(240,253,250,0.55),transparent_9%),radial-gradient(circle_at_26%_45%,rgba(165,243,252,0.46),transparent_8%),radial-gradient(circle_at_42%_54%,rgba(209,250,229,0.42),transparent_7%),radial-gradient(circle_at_60%_48%,rgba(186,230,253,0.44),transparent_8%),radial-gradient(circle_at_76%_55%,rgba(240,253,250,0.36),transparent_7%)] blur-[1px]"
                    style={{
                      animation:
                        'subtitleTsunamiFoamDrift 12s ease-in-out infinite',
                    }}
                  />

                  <div
                    className="absolute bottom-[18%] left-0 h-44 w-24 bg-gradient-to-t from-transparent via-cyan-100/30 to-transparent blur-xl"
                    style={{
                      animation:
                        'subtitleTsunamiCrashSweep 12s ease-in-out infinite',
                    }}
                  />

                  <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.08),rgba(2,6,23,0.2)_42%,rgba(2,6,23,0.42)_82%)]" />
                </div>

                <div className="relative z-10 flex h-full flex-col justify-between p-8 xl:p-10">
                  <div>
                    <div className="flex h-24 w-24 items-center justify-center rounded-[1.75rem] border border-cyan-300/20 bg-slate-950/45 p-4 shadow-2xl shadow-cyan-950/40 backdrop-blur-md">
                      <img
                        src="/BlueLogo.png"
                        alt="Subtitle Tsunami"
                        className="h-full w-full object-contain"
                      />
                    </div>

                    <p className="mt-8 text-xs font-black uppercase tracking-[0.32em] text-cyan-300">
                      Subtitle Tsunami Beta
                    </p>

                    <h2 className="mt-4 text-4xl font-black tracking-tight text-white">
                      {isSignInMode ? 'Welcome back.' : 'Start free.'}
                    </h2>

                    <p className="mt-5 max-w-xs text-sm leading-7 text-slate-300">
                      {isSignInMode
                        ? 'Sign in to continue processing your videos and downloading subtitle exports.'
                        : 'Create a free starter account and process one short video per day without adding a card.'}
                    </p>
                  </div>

                  <div className="rounded-3xl border border-emerald-300/20 bg-slate-950/62 p-5 shadow-2xl shadow-slate-950/30 backdrop-blur-md">
                    <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-300">
                      Free Starter Pack
                    </p>

                    <p className="mt-2 text-base font-black text-white">
                      $0 — no card needed
                    </p>

                    <ul className="mt-4 grid gap-2 text-sm leading-6 text-slate-300">
                      <li>✓ 1 video per day</li>
                      <li>✓ Up to 60 seconds</li>
                      <li>✓ Spanish subtitles</li>
                      <li>✓ Preview and download</li>
                    </ul>
                  </div>
                </div>
              </aside>

              <section className="max-h-[94vh] overflow-y-auto p-5 sm:p-6 lg:p-7 xl:p-8">
                <div className="mb-5 flex items-center gap-4 lg:hidden">
                  <div className="flex h-18 w-18 shrink-0 items-center justify-center rounded-3xl border border-cyan-300/20 bg-cyan-400/10 p-3 shadow-2xl shadow-cyan-950/30">
                    <img
                      src="/BlueLogo.png"
                      alt="Subtitle Tsunami"
                      className="h-14 w-14 object-contain"
                    />
                  </div>

                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">
                      Subtitle Tsunami
                    </p>

                    <h2 className="mt-1 text-xl font-black text-white">
                      {isSignInMode ? 'Sign in to continue' : 'Create a free account'}
                    </h2>

                    <p className="mt-1 text-xs leading-5 text-slate-400">
                      1 free video per day. No card needed.
                    </p>
                  </div>
                </div>

                <div className="mb-5 rounded-3xl border border-white/10 bg-white/[0.035] p-4 shadow-lg shadow-slate-950/20">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <p className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300">
                      What you get
                    </p>

                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                      Starter vs Subscribe
                    </p>
                  </div>

                  <div className="grid gap-3 lg:grid-cols-2">
                    <div className="rounded-2xl border border-emerald-300/15 bg-emerald-400/[0.06] p-4">
                      <p className="text-sm font-black text-emerald-100">
                        Free Starter Pack
                      </p>

                      <p className="mt-1 text-xs font-bold text-emerald-300">
                        $0 — no card needed
                      </p>

                      <ul className="mt-3 grid gap-1.5 text-xs leading-5 text-slate-300">
                        <li>✓ 1 video per day</li>
                        <li>✓ Up to 60 seconds</li>
                        <li>✓ Spanish subtitles</li>
                        <li>✓ Preview and download</li>
                      </ul>
                    </div>

                    <div className="rounded-2xl border border-cyan-300/15 bg-cyan-400/[0.06] p-4">
                      <p className="text-sm font-black text-cyan-100">
                        Subscribe
                      </p>

                      <p className="mt-1 text-xs font-bold text-cyan-300">
                        $4.99/month
                      </p>

                      <ul className="mt-3 grid gap-1.5 text-xs leading-5 text-slate-300">
                        <li>✓ 25 uploads per day</li>
                        <li>✓ Up to 60 seconds per upload</li>
                        <li>✓ Spanish subtitles</li>
                        <li>✓ Preview and download</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="mb-5 grid grid-cols-2 gap-2 rounded-2xl border border-white/10 bg-slate-950/70 p-1 shadow-lg shadow-slate-950/30">
                  <button
                    type="button"
                    onClick={() => setActiveMode('signup')}
                    className={[
                      'rounded-xl px-4 py-3 text-xs font-black uppercase tracking-[0.14em] transition',
                      !isSignInMode
                        ? 'border border-emerald-300/30 bg-emerald-400/15 text-emerald-100 shadow-lg shadow-emerald-950/20'
                        : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-100',
                    ].join(' ')}
                  >
                    Sign Up
                  </button>

                  <button
                    type="button"
                    onClick={() => setActiveMode('signin')}
                    className={[
                      'rounded-xl px-4 py-3 text-xs font-black uppercase tracking-[0.14em] transition',
                      isSignInMode
                        ? 'border border-cyan-300/30 bg-cyan-400/15 text-cyan-100 shadow-lg shadow-cyan-950/20'
                        : 'text-slate-400 hover:bg-white/[0.04] hover:text-slate-100',
                    ].join(' ')}
                  >
                    Sign In
                  </button>
                </div>

                <div className="rounded-3xl border border-white/10 bg-slate-950/70 p-1 shadow-2xl shadow-slate-950/30">
                  {isSignInMode ? (
                    <SignInBox
                      isUserSignedIn={isUserSignedIn}
                      handleLogout={handleLogout}
                      user={user}
                      setIsUserSignedIn={setIsUserSignedIn}
                      setUser={setUser}
                      setDoctors={setDoctors}
                      setInsurances={setInsurances}
                      setBodilyParts={setBodilyParts}
                      setOriginalDoctors={setOriginalDoctors}
                      onSuccessfulSignIn={handleSuccessfulSignIn}
                      variant="navbar"
                    />
                  ) : (
                    <SignUpBox
                      isUserSignedIn={isUserSignedIn}
                      handleLogout={handleLogout}
                      user={user}
                      setIsUserSignedIn={setIsUserSignedIn}
                      setUser={setUser}
                      setDoctors={setDoctors}
                      setInsurances={setInsurances}
                      setBodilyParts={setBodilyParts}
                      setOriginalDoctors={setOriginalDoctors}
                      onSuccessfulSignUp={handleSuccessfulSignUp}
                    />
                  )}
                </div>

                <p className="mt-4 text-center text-xs leading-5 text-slate-500">
                  Free starter access includes 1 short video per day. Subscribe for 25 uploads per day at $4.99/month. Each upload supports videos up to 60 seconds.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignInRequiredModal;